import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';

// Reusable "are you sure?" modal — the confirmation step in front of any
// destructive action (currently product deletes from the shop grid and the
// admin). Portals to <body> so it sits above the fixed header and admin
// chrome, mirrors the Lightbox overlay pattern (backdrop click + Escape to
// cancel, body scroll lock while open). No browser confirm() dialogs — those
// are blocked in the app shell and look nothing like the rest of the UI.
export default function ConfirmDialog({
  title = 'Are you sure?',
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  busyLabel = 'Deleting…',
  danger = true,
  busy = false,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onCancel, busy]);

  return createPortal(
    <div
      onClick={() => { if (!busy) onCancel(); }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(6, 5, 14, 0.82)',
        backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="sv-card"
        style={{ maxWidth: 420, width: '100%', padding: 24 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          {danger && (
            <span
              style={{
                width: 34, height: 34, borderRadius: 999, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'color-mix(in srgb, var(--danger) 16%, transparent)', color: 'var(--danger)',
              }}
            >
              <AlertTriangle size={18} />
            </span>
          )}
          <h2 style={{ fontSize: '1.1rem', fontWeight: 900, margin: 0 }}>{title}</h2>
        </div>

        {message && (
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.55, margin: '0 0 20px', fontSize: '0.92rem' }}>
            {message}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} disabled={busy} className="sv-btn-outline" style={{ padding: '10px 18px' }}>
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="sv-btn"
            style={{
              padding: '10px 18px',
              background: danger ? 'var(--danger)' : undefined,
              borderColor: danger ? 'var(--danger)' : undefined,
              color: danger ? '#fff' : undefined,
            }}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
